import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useToast } from '@/components/ui';
import { updateAccountEnabled } from '@/api/console';
import { OrgAdminApiError } from '@/api/orgAdmin';
import type { KeycloakAccountRow } from './types';

/**
 * EAA-1820 (frame 86, "Vô hiệu hoá tài khoản"): `PATCH /accounts/{sub}
 * {enabled}` — see `updateAccountEnabled`'s docstring for exactly why the
 * dialog carries no "Lý do"/note field the design mock shows (the server has
 * nowhere to put them).
 *
 * DESTRUCTIVE-ACTION RULE: disabling is the one direction that is destructive
 * (revokes every active session and blocks login everywhere), so it is the only
 * direction gated behind typing the account's username to confirm. Re-enabling
 * is the reversal, not a second destructive act, so it runs off a plain confirm
 * button — same asymmetry the repo's `ConfirmButton` embodies elsewhere.
 */
export function useDeactivateAccount(invalidate: () => void) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const toast = useToast();

    const [target, setTarget] = useState<KeycloakAccountRow | null>(null);
    const [confirmText, setConfirmText] = useState('');
    const [error, setError] = useState<string | null>(null);

    const open = useCallback((row: KeycloakAccountRow) => {
        setTarget(row);
        setConfirmText('');
        setError(null);
    }, []);
    const close = useCallback(() => setTarget(null), []);

    const mutation = useMutation({
        mutationFn: (enabled: boolean) => updateAccountEnabled(config.apiBase, accessToken as string, target?.sub as string, enabled),
        onSuccess: (_data, enabled) => {
            setTarget(null);
            invalidate();
            toast.success(
                enabled
                    ? t('console.accounts.deactivate.enableSuccess', { username: target?.username })
                    : t('console.accounts.deactivate.disableSuccess', { username: target?.username })
            );
        },
        onError: (err: unknown) => {
            setError(err instanceof OrgAdminApiError ? err.message : t('console.accounts.deactivate.errorFallback'));
        },
    });

    const confirmMatches = target ? confirmText.trim() === target.username : false;

    const submitDisable = useCallback(() => {
        if (!confirmMatches) return;
        setError(null);
        mutation.mutate(false);
    }, [confirmMatches, mutation]);

    const submitEnable = useCallback(() => {
        setError(null);
        mutation.mutate(true);
    }, [mutation]);

    return {
        target,
        open,
        close,
        confirmText,
        setConfirmText,
        confirmMatches,
        error,
        submitDisable,
        submitEnable,
        busy: mutation.isPending,
    };
}

export type DeactivateAccountVM = ReturnType<typeof useDeactivateAccount>;
